import { Component, DestroyRef, EventEmitter, Input, OnChanges, OnInit, Output, SimpleChanges, inject } from '@angular/core';
import { debounceTime } from "rxjs/operators";
import { MatButtonModule } from '@angular/material/button';
import { FormControl, FormsModule, ReactiveFormsModule } from '@angular/forms';
import { MatInput, MatInputModule } from '@angular/material/input';
import { MatFormField, MatFormFieldModule, MatLabel } from '@angular/material/form-field';
import { MatIconModule } from '@angular/material/icon';
import { NgClass, NgTemplateOutlet } from '@angular/common';
import { MatProgressSpinner } from '@angular/material/progress-spinner';
import { FolderService } from '../../services/folder.service';
import { SharepointFolderDto } from '../../../../../api/generated';
import { TranslateModule } from '@ngx-translate/core';
import { MatMenuModule } from "@angular/material/menu";
import { MatDialog } from '@angular/material/dialog';
import { FolderTreeDialogComponent } from '../folder-tree-dialog/folder-tree-dialog.component';
import { NgxSkeletonLoaderModule } from 'ngx-skeleton-loader';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';

@Component({
  selector: 'app-folder-tree',
  templateUrl: './folder-tree.component.html',
  styleUrls: ['./folder-tree.component.scss'],
  standalone: true,
  imports: [MatProgressSpinner,
    NgClass,
    MatIconModule,
    NgTemplateOutlet,
    MatFormField,
    MatLabel,
    MatInput,
    MatButtonModule,
    MatMenuModule,
    MatFormFieldModule, MatInputModule, FormsModule, ReactiveFormsModule,
    TranslateModule,
    NgxSkeletonLoaderModule]
})
export class FolderTreeComponent implements OnChanges, OnInit {
  private readonly destroyRef = inject(DestroyRef);
  @Input() loading: boolean = false;
  @Input() loadingMore: boolean = false;
  @Input() hasMoreFolders = false;
  readonly skeletonItems = Array.from({ length: 25 });
  readonly skeletonTheme = {
    height: '38px',
    'border-radius': '8px',
    margin: '0 0 6px 0'
  };
  private readonly scrollThreshold = 120;
  @Input() folders: SharepointFolderDto[] = [];
  @Input() siteId: string | null = null;
  @Input() selectedFolder: SharepointFolderDto | null = null;
  @Output() folderEvent = new EventEmitter<SharepointFolderDto | null>();
  @Output() loadMoreRequested = new EventEmitter<void>();
  @Output() createFolderRequested = new EventEmitter<{ parentFolder: SharepointFolderDto, folderName: string }>();

  filteredFolders: SharepointFolderDto[] = [];
  visibleFolders: SharepointFolderDto[] = [];

  constructor(
    private folderService: FolderService,
    private dialog: MatDialog
  ) { }

  ngOnInit(): void {
    this.searchControl.valueChanges.pipe(
      debounceTime(500),
      takeUntilDestroyed(this.destroyRef)
    ).subscribe(value => {
      const query = (value ?? '').trim();

      if (query.length > 0) {
        this.filteredFolders = this.folderService.filterFoldersByName(query, this.folders);

        if (this.selectedFolder) {
          const selectedFolder = this.folderService.findFolderById(this.selectedFolder.id, this.filteredFolders);
          if (!selectedFolder) {
            this.selectedFolder = null;
            this.folderEvent.emit(null);
          }
        }
      } else {
        this.filteredFolders = this.folders;
      }

      this.visibleFolders = [...this.filteredFolders];
    });

    this.filteredFolders = this.folders;
    this.visibleFolders = [...this.filteredFolders];
  }

  readonly searchControl = new FormControl('');

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['folders']) {
      this.filteredFolders = this.folders;
      const query = (this.searchControl.value ?? '').trim();
      this.visibleFolders = query.length > 0
        ? this.folderService.filterFoldersByName(query, this.folders)
        : [...this.folders];

      if (this.selectedFolder) {
        const selectedFolder = this.folderService.findFolderById(this.selectedFolder.id, this.filteredFolders);
        if (!selectedFolder) {
          this.selectedFolder = null;
          this.folderEvent.emit(null);
        }
      }
    }
  }

  onScroll(event: Event): void {
    if (this.loadingMore || !this.hasMoreFolders || (this.searchControl.value ?? '').trim().length > 0) {
      return;
    }

    const target = event.target as HTMLElement;
    const reachedBottom = target.scrollTop + target.clientHeight >= target.scrollHeight - this.scrollThreshold;
    if (reachedBottom) {
      this.loadMoreRequested.emit();
    }
  }

  onFolderClicked(folder: SharepointFolderDto): void {
    if (this.selectedFolder?.id === folder.id) {
      this.selectedFolder = null;
      this.folderEvent.emit(null);
    } else {
      this.selectedFolder = folder;
      this.folderEvent.emit(folder);
    }
  }

  openFolderDialog(menuTrigger: any, folder: SharepointFolderDto): void {
    if (menuTrigger && typeof menuTrigger.closeMenu === 'function') {
      menuTrigger.closeMenu();
    }
    const dialogRef = this.dialog.open(FolderTreeDialogComponent);
    dialogRef.afterClosed().subscribe(result => {
      if (result) {
        this.createFolderRequested.emit({
          parentFolder: folder,
          folderName: String(result).trim()
        });
      }
    });
  }

  openInNewTab(url: string) {
    if (url) {
      window.open(url, '_blank');
    }
  }

}
